import type {
  BeachGradient,
  CoastalBreakType,
  CoastalLocationConfig,
  CoastalProfile,
} from "./coastal-types";
import type { SupProfileKey } from "../sport-profiles";

export type CoastalVisualTreatment = Readonly<{
  profile: SupProfileKey;
  hudSport: SupProfileKey;
  sceneSport: "sup" | "surf";
}>;

const SUP_VISUAL_TREATMENTS: Record<SupProfileKey, CoastalVisualTreatment> = {
  "sup-touring": {
    profile: "sup-touring",
    hudSport: "sup-touring",
    sceneSport: "sup",
  },
  "sup-surf": {
    profile: "sup-surf",
    hudSport: "sup-surf",
    sceneSport: "surf",
  },
  "sup-downwind": {
    profile: "sup-downwind",
    hudSport: "sup-downwind",
    sceneSport: "sup",
  },
};

export function visualTreatmentForSport(
  profile: SupProfileKey,
): CoastalVisualTreatment {
  return SUP_VISUAL_TREATMENTS[profile];
}

function beachProfile(gradient: BeachGradient): CoastalProfile {
  if (gradient === "gentle") {
    return {
      id: "gentle-beach",
      breakType: "beach",
      gradient,
      shoreSlope: 0.035,
      shorelineTravelM: 72,
      breakAnchorsM: [34, 58, 86],
      breakerStyle: "spilling",
      camera: { position: [8.6, 7.4, 14.5], target: [0, 0.15, -8.8] },
    };
  }
  if (gradient === "steep") {
    return {
      id: "steep-beach",
      breakType: "beach",
      gradient,
      shoreSlope: 0.12,
      shorelineTravelM: 22,
      breakAnchorsM: [24, 48],
      breakerStyle: "plunging",
      camera: { position: [8.2, 7.2, 14], target: [0, 0.3, -8] },
    };
  }
  return {
    id: "moderate-beach",
    breakType: "beach",
    gradient: "moderate",
    shoreSlope: 0.075,
    shorelineTravelM: 40,
    breakAnchorsM: [28, 54],
    breakerStyle: "spilling",
    camera: { position: [8.4, 7.3, 14.2], target: [0, 0.2, -8.5] },
  };
}

const POINT_PROFILE: CoastalProfile = {
  id: "moderate-point",
  breakType: "point",
  gradient: "moderate",
  shoreSlope: 0.08,
  shorelineTravelM: 34,
  breakAnchorsM: [30, 62],
  breakerStyle: "peeling",
  camera: { position: [9, 7.5, 14.5], target: [-1, 0.4, -9] },
};

const REEF_PROFILE: CoastalProfile = {
  id: "steep-reef",
  breakType: "reef",
  gradient: "steep",
  shoreSlope: 0.1,
  shorelineTravelM: 20,
  breakAnchorsM: [45],
  breakerStyle: "plunging",
  camera: { position: [8.5, 7.6, 14.4], target: [0, 0.5, -10] },
};

export const COASTAL_PROFILES = {
  "gentle-beach": beachProfile("gentle"),
  "moderate-beach": beachProfile("moderate"),
  "steep-beach": beachProfile("steep"),
  "moderate-point": POINT_PROFILE,
  "steep-reef": REEF_PROFILE,
} as const satisfies Record<string, CoastalProfile>;

export function profileForLocation(location: CoastalLocationConfig): CoastalProfile {
  const breakType: CoastalBreakType = location.breakType ?? "beach";
  if (breakType === "point") return COASTAL_PROFILES["moderate-point"];
  if (breakType === "reef" || breakType === "slab") return COASTAL_PROFILES["steep-reef"];
  const gradient = location.beachGradient ?? "moderate";
  return COASTAL_PROFILES[`${gradient}-beach`];
}
